import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { ChevronRight, Users, DollarSign, Trophy, MessageCircle, BarChart3, Smartphone, Check } from 'lucide-react';

const GAME_NAMES = ['Nassau', 'Skins', 'Wolf', 'Best Ball', 'Scramble', 'Match Play', 'Stableford', 'Hammer', 'Dots', 'Trash'];

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyState, setNotifyState] = useState('idle'); // idle | sending | done | error

  const submitNotify = async (e) => {
    e.preventDefault();
    if (!notifyEmail.trim() || notifyState === 'sending') return;
    setNotifyState('sending');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/launch-notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: notifyEmail }),
      });
      const data = await res.json();
      setNotifyState(data.status === 'ok' ? 'done' : 'error');
    } catch {
      setNotifyState('error');
    }
  };

  useEffect(() => {
    if (isAuthenticated) navigate('/social');
  }, [isAuthenticated, navigate]);

  return (
    <div className="min-h-screen" style={{ background: 'var(--club-cream, #F3F0E8)' }} data-testid="landing-page">

      {/* ===== NAV ===== */}
      <nav className="fixed top-0 left-0 right-0 z-50" style={{ background: 'rgba(243,240,232,0.85)', backdropFilter: 'blur(16px)', borderBottom: '1px solid var(--fairway-sand, #E7DDC7)' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-12 flex items-center justify-between h-16">
          <img src="/logo-the-club.png" alt="The Club" className="h-10 w-auto" data-testid="landing-logo" />
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-sm font-medium rounded-sm" style={{ color: 'var(--club-green, #0F3B2E)' }} data-testid="login-btn">
              Log In
            </Button>
            <Button onClick={() => navigate('/register')} className="rounded-sm px-5 text-sm font-semibold" style={{ background: 'var(--club-green, #0F3B2E)', color: '#fff' }} data-testid="signup-btn">
              Join The Club
            </Button>
          </div>
        </div>
      </nav>

      {/* ===== HERO ===== */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1743185836009-848e5035422b?auto=format&fit=crop&w=1920&q=80"
            alt="Golf course at sunset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(8,36,28,0.82) 0%, rgba(15,59,46,0.55) 60%, rgba(8,36,28,0.4) 100%)' }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-12 py-32 w-full">
          <div className="max-w-2xl">
            <p className="text-sm font-sans uppercase tracking-[0.25em] mb-6" style={{ color: 'var(--gold, #c9a94e)' }} data-testid="hero-tag">
              Golf. Social. Competition.
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl font-bold leading-[1.05] tracking-tight mb-6 text-white">
              Your Crew.<br />Your Games.<br />
              <span style={{ color: 'var(--gold, #c9a94e)' }}>Your Money.</span>
            </h1>
            <p className="text-base sm:text-lg leading-relaxed mb-10 max-w-lg" style={{ color: 'rgba(243,240,232,0.8)' }}>
              The Club connects golfers, runs your side games, and settles up — so you can focus on the round.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={() => navigate('/register')} className="rounded-sm px-8 py-6 text-base font-semibold shadow-xl transition-all hover:scale-[1.02]" style={{ background: 'var(--gold, #c9a94e)', color: 'var(--deep-forest, #1a2332)' }} data-testid="hero-cta-btn">
                Get Started Free <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate('/login')} className="rounded-sm px-8 py-6 text-base font-semibold" style={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fff', background: 'rgba(255,255,255,0.08)' }}>
                I Have an Account
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== GAME TYPE MARQUEE ===== */}
      <div className="overflow-hidden py-5" style={{ background: 'var(--deep-forest, #1a2332)' }}>
        <div className="flex animate-marquee whitespace-nowrap">
          {[...GAME_NAMES, ...GAME_NAMES, ...GAME_NAMES].map((name, i) => (
            <span key={`${name}-${i}`} className="mx-8 font-serif text-lg sm:text-xl font-semibold" style={{ color: 'var(--gold, #c9a94e)', opacity: 0.7 }}>
              {name}
            </span>
          ))}
        </div>
      </div>

      {/* ===== SOCIAL SECTION ===== */}
      <section className="py-24 sm:py-32 px-5 sm:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-sm font-sans uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--gold, #c9a94e)' }}>Find Your People</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight mb-6" style={{ color: 'var(--deep-forest, #1a2332)' }}>
              Golf Is Better Together
            </h2>
            <p className="text-base leading-relaxed mb-8 max-w-lg" style={{ color: 'var(--warm-gray, #6B7280)' }}>
              Whether you need a fourth, want a regular weekend group, or just want to talk golf — The Club makes it easy to find golfers who play like you do.
            </p>
            <div className="space-y-5">
              {[
                { icon: Users, title: 'Discover & Match', desc: 'Swipe through golfers filtered by skill level, pace, and location. Match with players who fit your game.' },
                { icon: MessageCircle, title: 'Direct Messages', desc: 'Message anyone directly. Plan rounds, talk trash, and build your crew.' },
                { icon: BarChart3, title: 'Social Feed', desc: 'Share rounds, post photos, follow friends, and see what your group is up to.' },
              ].map((f) => (
                <div key={f.title} className="flex gap-4 items-start">
                  <div className="w-11 h-11 rounded-sm flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(15,59,46,0.08)' }}>
                    <f.icon className="w-5 h-5" style={{ color: 'var(--club-green, #0F3B2E)' }} />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg font-semibold mb-1" style={{ color: 'var(--deep-forest)' }}>{f.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--warm-gray)' }}>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative hidden lg:block">
            <div className="relative max-w-sm mx-auto">
              <div className="absolute -top-4 -left-4 right-4 bottom-4 rounded-sm" style={{ background: 'var(--fairway-sand, #E7DDC7)' }} />
              <img
                src="https://images.unsplash.com/photo-1621369132713-4cfdb898dfcb?auto=format&fit=crop&w=600&q=80"
                alt="Golfers socializing"
                className="relative rounded-sm w-full aspect-[3/4] object-cover"
                style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== SIDE GAMES / BETTING SECTION ===== */}
      <section className="py-24 sm:py-32 px-5 sm:px-12" style={{ background: 'var(--deep-forest, #1a2332)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-sans uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--gold, #c9a94e)' }}>Side Games</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight mb-4 text-white">
              Run Your Games.<br />We Handle the Math.
            </h2>
            <p className="text-base max-w-xl mx-auto" style={{ color: 'rgba(243,240,232,0.6)' }}>
              11 game types. Live hole-by-hole scoring. Automatic settlement. No more "who owes who?" at the 19th hole.
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {/* Large card — Nassau */}
            <div className="col-span-2 row-span-2 p-6 sm:p-8 rounded-sm flex flex-col justify-between" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="bento-nassau">
              <div>
                <span className="font-serif text-4xl sm:text-5xl font-black" style={{ color: 'var(--gold)' }}>3x</span>
                <h3 className="font-serif text-2xl sm:text-3xl font-bold text-white mt-2">Nassau</h3>
                <p className="text-sm mt-2" style={{ color: 'rgba(243,240,232,0.5)' }}>Front 9. Back 9. Overall. Three bets in one with presses.</p>
              </div>
              <div className="flex gap-2 mt-6">
                <span className="px-3 py-1 rounded-sm text-xs font-semibold" style={{ background: 'rgba(200,169,106,0.15)', color: 'var(--gold)' }}>Teams</span>
                <span className="px-3 py-1 rounded-sm text-xs font-semibold" style={{ background: 'rgba(200,169,106,0.15)', color: 'var(--gold)' }}>Presses</span>
              </div>
            </div>

            {/* Skins */}
            <div className="p-5 rounded-sm flex flex-col justify-between" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <span className="font-serif text-2xl font-black" style={{ color: 'var(--gold)' }}>$</span>
              <div className="mt-3">
                <h3 className="font-serif text-lg font-bold text-white">Skins</h3>
                <p className="text-xs mt-1" style={{ color: 'rgba(243,240,232,0.4)' }}>Lowest score wins. Ties carry.</p>
              </div>
            </div>

            {/* Wolf */}
            <div className="p-5 rounded-sm flex flex-col justify-between" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <span className="font-serif text-2xl font-black" style={{ color: 'var(--gold)' }}>W</span>
              <div className="mt-3">
                <h3 className="font-serif text-lg font-bold text-white">Wolf</h3>
                <p className="text-xs mt-1" style={{ color: 'rgba(243,240,232,0.4)' }}>Pick a partner — or go lone.</p>
              </div>
            </div>

            {/* Best Ball */}
            <div className="p-5 rounded-sm flex flex-col justify-between" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <span className="font-serif text-2xl font-black" style={{ color: 'var(--gold)' }}>BB</span>
              <div className="mt-3">
                <h3 className="font-serif text-lg font-bold text-white">Best Ball</h3>
                <p className="text-xs mt-1" style={{ color: 'rgba(243,240,232,0.4)' }}>Best score counts for the team.</p>
              </div>
            </div>

            {/* Scramble */}
            <div className="p-5 rounded-sm flex flex-col justify-between" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <span className="font-serif text-2xl font-black" style={{ color: 'var(--gold)' }}>SC</span>
              <div className="mt-3">
                <h3 className="font-serif text-lg font-bold text-white">Scramble</h3>
                <p className="text-xs mt-1" style={{ color: 'rgba(243,240,232,0.4)' }}>Play from the best shot.</p>
              </div>
            </div>

            {/* Wide card — Live Scoring */}
            <div className="col-span-2 p-5 sm:p-6 rounded-sm" style={{ background: 'rgba(200,169,106,0.1)', border: '1px solid rgba(200,169,106,0.2)' }}>
              <div className="flex items-center gap-3 mb-3">
                <DollarSign className="w-5 h-5" style={{ color: 'var(--gold)' }} />
                <h3 className="font-serif text-lg font-bold text-white">Live Scoring & Settlement</h3>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(243,240,232,0.6)' }}>
                Enter scores hole by hole. Watch the leaderboard update in real time. When you're done, the app calculates exactly who owes who — down to the penny.
              </p>
            </div>

            {/* +6 more */}
            <div className="col-span-2 p-5 rounded-sm flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="text-sm font-serif" style={{ color: 'rgba(243,240,232,0.4)' }}>
                + Hammer, Dots, Trash, Match Play, Stableford, Bingo Bango Bongo
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== LEADERBOARD SECTION ===== */}
      <section className="py-24 sm:py-32 px-5 sm:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1">
            {/* Mock leaderboard */}
            <div className="rounded-sm overflow-hidden" style={{ border: '1px solid var(--fairway-sand)', background: '#fff' }}>
              <div className="px-5 py-4" style={{ borderBottom: '1px solid var(--fairway-sand)' }}>
                <h3 className="font-serif text-lg font-bold" style={{ color: 'var(--deep-forest)' }}>Season Leaderboard</h3>
              </div>
              <div>
                {[
                  { rank: 1, name: 'Tiger W.', hcp: '0.2', pnl: '+$247', wins: 12 },
                  { rank: 2, name: 'Marc V.', hcp: '8.3', pnl: '+$182', wins: 9 },
                  { rank: 3, name: 'Phil M.', hcp: '5.1', pnl: '+$94', wins: 7 },
                  { rank: 4, name: 'DJ P.', hcp: '3.0', pnl: '-$42', wins: 4 },
                ].map((p) => (
                  <div key={p.rank} className="flex items-center gap-4 px-5 py-3" style={{ borderBottom: '1px solid var(--fairway-sand)' }}>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black ${p.rank === 1 ? 'text-white' : 'text-muted-foreground'}`} style={p.rank === 1 ? { background: 'var(--club-green)' } : { background: 'var(--fairway-sand)' }}>
                      {p.rank}
                    </span>
                    <div className="flex-1">
                      <p className="font-semibold text-sm" style={{ color: 'var(--deep-forest)' }}>{p.name}</p>
                      <p className="text-xs" style={{ color: 'var(--warm-gray)' }}>{p.hcp} Hcp · {p.wins} wins</p>
                    </div>
                    <span className={`text-sm font-bold ${p.pnl.startsWith('+') ? 'text-green-700' : 'text-red-600'}`}>{p.pnl}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-sm font-sans uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--gold, #c9a94e)' }}>Stats & Leaderboard</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight mb-6" style={{ color: 'var(--deep-forest, #1a2332)' }}>
              Know Where You Stand
            </h2>
            <p className="text-base leading-relaxed mb-8 max-w-lg" style={{ color: 'var(--warm-gray)' }}>
              Track your season P&L, holes won, birdies, and Strokes to Par — all automatically. The leaderboard keeps your crew honest.
            </p>
            <div className="flex flex-wrap gap-6">
              {[
                { icon: Trophy, label: 'Strokes to Par', sub: 'Auto from your rounds' },
                { icon: BarChart3, label: 'Full Stats', sub: 'Birdies, wins, avg score' },
              ].map((s) => (
                <div key={s.label} className="flex gap-3 items-start">
                  <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ background: 'rgba(15,59,46,0.08)' }}>
                    <s.icon className="w-5 h-5" style={{ color: 'var(--club-green)' }} />
                  </div>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: 'var(--deep-forest)' }}>{s.label}</p>
                    <p className="text-xs" style={{ color: 'var(--warm-gray)' }}>{s.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOUNDING MEMBER CTA ===== */}
      <section className="py-24 sm:py-32 px-5 sm:px-12" style={{ background: 'var(--club-green, #0F3B2E)' }}>
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm font-sans uppercase tracking-[0.25em] mb-6" style={{ color: 'var(--gold, #c9a94e)' }}>Founding Members</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight mb-6 text-white">
            Everything. Free. Forever.
          </h2>
          <p className="text-base leading-relaxed mb-10 max-w-lg mx-auto" style={{ color: 'rgba(243,240,232,0.7)' }}>
            The first 1,000 members get lifetime Platinum access — every feature we build, current and future, at no cost. Ever.
          </p>
          <Button size="lg" onClick={() => navigate('/register')} className="rounded-sm px-10 py-7 text-base font-bold shadow-xl transition-all hover:scale-[1.02]" style={{ background: 'var(--gold, #c9a94e)', color: 'var(--deep-forest, #1a2332)' }} data-testid="cta-signup-btn">
            Claim Your Spot <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
          <p className="text-xs mt-6" style={{ color: 'rgba(243,240,232,0.35)' }}>No credit card. No catch. Just golf.</p>
        </div>
      </section>

      {/* ===== APP LAUNCH NOTIFY ===== */}
      <section className="py-16 px-5 sm:px-12" style={{ background: 'var(--deep-forest, #1a2332)', borderTop: '1px solid rgba(255,255,255,0.06)' }} data-testid="launch-notify-section">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Smartphone className="w-5 h-5" style={{ color: 'var(--gold, #c9a94e)' }} />
            <p className="text-sm font-sans uppercase tracking-[0.25em]" style={{ color: 'var(--gold, #c9a94e)' }}>Coming to iOS & Android</p>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight mb-3 text-white">
            Be First on the Tee
          </h2>
          <p className="text-sm sm:text-base mb-8 max-w-md mx-auto" style={{ color: 'rgba(243,240,232,0.6)' }}>
            The Club is coming to the App Store and Google Play. Drop your email and we'll send you the download link the moment we launch.
          </p>
          {notifyState === 'done' ? (
            <div className="flex items-center justify-center gap-2 py-3" data-testid="notify-success-message">
              <Check className="w-5 h-5" style={{ color: 'var(--gold, #c9a94e)' }} />
              <p className="text-base font-semibold" style={{ color: 'var(--gold, #c9a94e)' }}>You're on the list. See you at launch!</p>
            </div>
          ) : (
            <form onSubmit={submitNotify} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                required
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-1 px-4 py-3 rounded-sm text-base outline-none"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff' }}
                data-testid="notify-email-input"
              />
              <Button type="submit" disabled={notifyState === 'sending'} className="rounded-sm px-6 py-3 text-base font-semibold" style={{ background: 'var(--gold, #c9a94e)', color: 'var(--deep-forest, #1a2332)' }} data-testid="notify-submit-btn">
                {notifyState === 'sending' ? 'Sending…' : 'Notify Me'}
              </Button>
            </form>
          )}
          {notifyState === 'error' && (
            <p className="text-sm mt-3" style={{ color: '#f0a0a0' }} data-testid="notify-error-message">Something went wrong — please check your email and try again.</p>
          )}
        </div>
      </section>

      {/* ===== ABOUT & CONTACT ===== */}
      <section className="py-24 sm:py-28 px-5 sm:px-12" data-testid="about-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-sm font-sans uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--gold, #c9a94e)' }}>About Us</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight mb-6" style={{ color: 'var(--deep-forest, #1a2332)' }}>
              Built by Golfers,<br />for Golfers
            </h2>
            <p className="text-base leading-relaxed mb-5 max-w-lg" style={{ color: 'var(--warm-gray, #6B7280)' }}>
              The Club is a golf social platform developed and operated by <strong style={{ color: 'var(--deep-forest)' }}>Clubhouse Labs, LLC</strong>. We started with a simple frustration: finding a game, keeping score in side games, and settling up afterward was harder than it should be.
            </p>
            <p className="text-base leading-relaxed max-w-lg" style={{ color: 'var(--warm-gray, #6B7280)' }}>
              So we built the app we wanted in our own foursome — golfer matchmaking, real-time hole-by-hole scoring for 11 classic game formats, automatic settlement, and a social feed to keep your crew connected between rounds. The Club is available on the web today, with native iOS and Android apps on the way.
            </p>
          </div>
          <div className="lg:pt-16">
            <div className="rounded-sm p-8 sm:p-10" style={{ background: '#fff', border: '1px solid var(--fairway-sand, #E7DDC7)' }} data-testid="contact-card">
              <p className="text-sm font-sans uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--gold, #c9a94e)' }}>Contact</p>
              <h3 className="font-serif text-2xl font-bold mb-6" style={{ color: 'var(--deep-forest)' }}>Get in Touch</h3>
              <div className="space-y-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--warm-gray)' }}>Support & General Inquiries</p>
                  <a href="mailto:support@theclubgolf.co" className="text-base font-semibold hover:underline" style={{ color: 'var(--club-green, #0F3B2E)' }} data-testid="contact-support-email">
                    support@theclubgolf.co
                  </a>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--warm-gray)' }}>Company</p>
                  <p className="text-base" style={{ color: 'var(--deep-forest)' }}>Clubhouse Labs, LLC</p>
                  <p className="text-sm" style={{ color: 'var(--warm-gray)' }}>Operator of The Club — theclubgolf.co</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--warm-gray)' }}>Legal & Policies</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: 'var(--club-green)' }}>Privacy Policy</a>
                    <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: 'var(--club-green)' }}>Terms of Service</a>
                    <a href="/community-guidelines.html" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: 'var(--club-green)' }}>Community Guidelines</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="py-10 px-5 sm:px-12" style={{ borderTop: '1px solid var(--fairway-sand, #E7DDC7)' }}>
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-4 text-center">
          <img src="/logo-the-club.png" alt="The Club Golf" className="h-8 w-auto" />
          <p className="text-xs" style={{ color: 'var(--warm-gray)' }}>
            The Club Golf is owned and operated by Clubhouse Labs, LLC. &nbsp;·&nbsp; <a href="mailto:support@theclubgolf.co" className="hover:underline" data-testid="footer-support-email">support@theclubgolf.co</a>
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs" style={{ color: 'var(--warm-gray)' }}>
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="footer-privacy-link">Privacy Policy</a>
            <span aria-hidden="true">·</span>
            <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="footer-terms-link">Terms of Service</a>
            <span aria-hidden="true">·</span>
            <a href="/contact.html" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="footer-contact-link">Contact</a>
            <span aria-hidden="true">·</span>
            <a href="/delete-account.html" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="footer-delete-account-link">Delete Account</a>
          </div>
          <p className="text-xs" style={{ color: 'var(--warm-gray)' }}>&copy; 2026 Clubhouse Labs, LLC. All rights reserved.</p>
        </div>
      </footer>

      {/* Marquee animation */}
      <style>{`
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-33.33%); } }
        .animate-marquee { animation: marquee 30s linear infinite; }
      `}</style>
    </div>
  );
};

export default Landing;
